import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { cacheSync } from '@/lib/cacheSync';
import { cachePatch, useOptimisticMutation } from '@/lib/optimistic-mutation';
import { applyMarks } from '@/lib/attendanceRegister';
import { useHomeroomScopeStore } from '@/store/homeroomScopeStore';
// Shared with the student roster's filter and the student form's picker, so all
// three offer the same groups. See hooks/useHomerooms.ts.
import { useHomerooms } from '@/hooks/useHomerooms';
import type {
  AttendanceReport,
  AttendanceDeskEntry,
  AttendanceDeskEntryRef,
  AttendanceMark,
  AttendanceOverview,
  AttendanceOverviewDay,
  AttendanceRegister,
  Homeroom,
} from '@/types/api';
import { attendanceKeys } from '@/lib/queryKeys'

/**
 * Re-exported from `lib/queryKeys`, which owns the whole key space — see the note
 * there. A query still names its key from the module it belongs to.
 */
export { attendanceKeys }

/**
 * The office's log across a range of days (design 1b).
 *
 * A range rather than a day because the screen needs one: the day strip shows a
 * week of chips, each marked with whether anything on it still needs an ID
 * check, and asking per chip would be five requests to draw one control. It also
 * makes stepping between the chips instant — the day the office clicks is
 * already in hand.
 */
export function useAttendanceOverview(from: string, to: string) {
  return useQuery({
    queryKey: attendanceKeys.overview(from, to),
    queryFn: async () => {
      const res = await api.get<AttendanceOverview>(
        `/api/attendance/overview?from=${from}&to=${to}`
      );
      return res.data;
    },
  });
}

/** What a report covers: a range, and the whole school or one homeroom. */
export interface AttendanceReportScope {
  from: string;
  to: string;
  /** Null for the whole school, which only the office may ask for. */
  homeroomId: string | null;
  /** Lists the year's homerooms that have no registers in the range yet. */
  academicYearId: string | null;
}

/**
 * Attendance totals over a range, per child and per homeroom.
 *
 * Held off until the scope is real: a teacher's page has no homeroom to ask
 * about until the homeroom list lands, and asking without one is the
 * whole-school report the API refuses them.
 */
export function useAttendanceReport(scope: AttendanceReportScope | null) {
  return useQuery({
    queryKey: attendanceKeys.report(
      scope ?? { from: '', to: '', homeroomId: null, academicYearId: null }
    ),
    enabled: scope !== null,
    queryFn: async () => {
      const params = new URLSearchParams({ from: scope!.from, to: scope!.to });
      if (scope!.homeroomId) params.set('homeroomId', scope!.homeroomId);
      if (scope!.academicYearId) params.set('academicYearId', scope!.academicYearId);
      const res = await api.get<AttendanceReport>(`/api/attendance/report?${params}`);
      return res.data;
    },
  });
}

/**
 * Logging one hand-off at the desk.
 *
 * Every attendance query is invalidated afterwards, not just the Overview's:
 * a desk entry writes into the same roll-call row a teacher is looking at, so
 * the register on another screen has just become wrong in exactly the way the
 * "Front desk" chip exists to show. Not optimistic, unlike the register's save —
 * this is a form somebody submits once, and a row that appeared before the
 * server accepted it would be a hand-off the office believes it recorded.
 *
 * The child's *own* attendance tab is the third reader, and it was the one being
 * missed: it is keyed under `studentAttendanceKeys`, a separate root, because it
 * reads the same table down the other axis — one child across a term rather than
 * one group across a day. `cacheSync.attendance` names both.
 */
export function useLogDeskEntry() {
  return useOptimisticMutation<AttendanceOverviewDay, Error, AttendanceDeskEntry>({
    mutationFn: async (entry: AttendanceDeskEntry) => {
      const res = await api.post<{ day: AttendanceOverviewDay }>('/api/attendance/desk', entry);
      return res.data.day;
    },
    invalidateKeys: cacheSync.attendance(),
  });
}

/**
 * Taking one hand-off back off the log — logged against the wrong child, or on
 * the wrong day. Not optimistic for the reason logging one is not, and every
 * attendance reader is told for the same reason: the entry is half of a row a
 * register is showing.
 */
export function useCancelDeskEntry() {
  return useOptimisticMutation<AttendanceOverviewDay, Error, AttendanceDeskEntryRef>({
    mutationFn: async (entry: AttendanceDeskEntryRef) => {
      const res = await api.post<{ day: AttendanceOverviewDay }>(
        '/api/attendance/desk/cancel',
        entry
      );
      return res.data.day;
    },
    invalidateKeys: cacheSync.attendance(),
  });
}

/**
 * Takes marks off one register's day, leaving those children not marked.
 *
 * Not optimistic: it is confirmed first, so nobody is waiting on a tap, and a
 * mark that vanished before the server agreed would be a day the teacher
 * believes is gone. The answer is the whole register, written straight into the
 * cache as the save's is, so only the *other* readers are invalidated.
 */
export function useClearAttendance(homeroomId: string | null, date: string) {
  const queryClient = useQueryClient();
  const queryKey = attendanceKeys.register(homeroomId ?? '', date);

  return useOptimisticMutation<AttendanceRegister, Error, string[]>({
    mutationFn: async (studentIds: string[]) => {
      const res = await api.post<AttendanceRegister>('/api/attendance/clear', {
        homeroomId,
        date,
        studentIds,
      });
      return res.data;
    },
    onSuccess: (register) => {
      queryClient.setQueryData(queryKey, register);
    },
    invalidateKeys: cacheSync.attendanceExceptRegisters(),
  });
}

/**
 * One homeroom's register for one day.
 *
 * Disabled until there is a homeroom to ask about, so a school with none makes
 * no request at all rather than a request that can only 400.
 */
export function useAttendanceRegister(homeroomId: string | null, date: string) {
  return useQuery({
    queryKey: attendanceKeys.register(homeroomId ?? '', date),
    enabled: Boolean(homeroomId),
    queryFn: async () => {
      const res = await api.get<AttendanceRegister>(
        `/api/attendance?homeroomId=${homeroomId}&date=${date}`
      );
      return res.data;
    },
  });
}

/**
 * Saves marks, shows them at once, and takes the server's register as the truth
 * the moment it lands.
 *
 * The mark is painted from `applyMarks` in `onMutate` rather than waited for. A
 * teacher walks a room and taps a row a second; a register that only shows the
 * tap a round trip later is one they tap again, and the second tap on a status
 * they already chose is how a present becomes an absent. `applyMarks` reproduces
 * the two rules the route applies — the times are normalised against the mark,
 * the five tiles are recounted — so the prediction is what the response will say
 * rather than an approximation of it, and nothing visibly changes underneath the
 * teacher when the response arrives.
 *
 * The response is still written straight into the cache rather than triggering a
 * refetch: the PUT already answers with the whole register, so refetching would
 * be a second round trip for an answer just received — and in between, the five
 * counts would be one save out of date while the row beneath them was not.
 * `onSuccess` is the resync for *this* register, which is why `invalidateKeys`
 * names `cacheSync.attendanceExceptRegisters()` and not `attendanceKeys.all` —
 * the office's overview and the child's own attendance tab are separate caches
 * reading the same marks, and they were being left with the register as it stood
 * before the teacher's tap.
 *
 * Still scoped, so the saves run one at a time. Each answer is the *whole*
 * register as of the moment the server built it, and it is written over the
 * cache wholesale; two in flight at once therefore means the slower one lands
 * last and puts back a register that predates the faster one. The scope queues
 * the *request* only — query-core runs `onMutate` before the scope gate — so the
 * second tap still paints immediately while its save waits its turn.
 *
 * Which is why an answer is not written over the cache bare: it was built before
 * the taps still queued behind it existed, so writing it as it stands takes
 * those marks straight back off the rows the teacher just tapped — the flicker
 * this hook exists to remove, arriving by a different route. The queued marks
 * are re-applied over it, so the roster and the counts are the server's while
 * the marks not yet sent stay where the teacher put them; each of those saves
 * then answers with them in turn.
 */
export function useSaveAttendance(homeroomId: string | null, date: string) {
  const queryClient = useQueryClient();
  const queryKey = attendanceKeys.register(homeroomId ?? '', date);
  const scopeId = `attendance-save-${homeroomId ?? 'none'}-${date}`;

  /**
   * The marks of every save for this register that has not been answered yet,
   * oldest first — this one excluded, since the response already carries it.
   *
   * Read at the moment it is needed rather than tracked: query-core keeps the
   * queue, and a save is `pending` from `onMutate` until after its own
   * `onSettled`, which is exactly the window in which its marks are on screen
   * and not yet in any response.
   */
  const marksStillQueued = (mine: AttendanceMark[]) =>
    queryClient
      .getMutationCache()
      .findAll({ status: 'pending', predicate: (m) => m.options.scope?.id === scopeId })
      .flatMap((m) => {
        const marks = m.state.variables as AttendanceMark[] | undefined;
        return marks && marks !== mine ? marks : [];
      });

  return useOptimisticMutation<AttendanceRegister, Error, AttendanceMark[]>({
    scope: { id: scopeId },
    mutationFn: async (marks: AttendanceMark[]) => {
      const res = await api.put<AttendanceRegister>('/api/attendance', {
        homeroomId,
        date,
        marks,
      });
      return res.data;
    },
    // Exact: this patch is one homeroom's one day. A prefix match would apply
    // today's marks to yesterday's register as well, which is cached whenever
    // the teacher has stepped back a day.
    optimistic: (marks) => [
      cachePatch<AttendanceRegister>(queryKey, (register) => applyMarks(register, marks), {
        exact: true,
      }),
    ],
    onSuccess: (register, marks) => {
      queryClient.setQueryData(queryKey, applyMarks(register, marksStillQueued(marks)));
    },
    // A refusal rolls back to the snapshot this save took, which predates every
    // tap made since — so the same re-application puts those back. This save's
    // own marks are not among them: it is the one that was refused.
    onError: (_error, marks) => {
      queryClient.setQueryData<AttendanceRegister>(queryKey, (register) =>
        register ? applyMarks(register, marksStillQueued(marks)) : register
      );
    },
    invalidateKeys: cacheSync.attendanceExceptRegisters(),
  });
}

export interface HomeroomScope {
  /** The groups this person may take a register for, which is what the pill offers. */
  homerooms: Homeroom[];
  /** The homeroom in scope, resolved: the stored one if it is still on the list. */
  homeroom: Homeroom | null;
  selectHomeroom: (homeroomId: string) => void;
  isLoading: boolean;
  /**
   * Whether the school runs any homerooms at all, whoever tutors them.
   *
   * Only the empty state needs it, and it needs it badly: "no homerooms yet, go
   * and make one" is the wrong sentence — and the wrong instruction — for a
   * teacher at a school with twelve of them who tutors none.
   */
  schoolHasHomerooms: boolean;
}

/**
 * Which homeroom the session is pointed at.
 *
 * The stored id is only ever a *candidate*, exactly as in `useCourseScope`:
 * it is resolved against the list actually fetched, and nothing writes back
 * during render. A stale id simply does not win, which is what keeps a year
 * switch, a renamed group and a first visit all landing on a register that
 * exists rather than on an empty one.
 */
export function useHomeroomScope(): HomeroomScope {
  const selectedHomeroomId = useHomeroomScopeStore((state) => state.selectedHomeroomId);
  const setSelectedHomeroomId = useHomeroomScopeStore((state) => state.setSelectedHomeroomId);
  const { data: allHomerooms = [], isLoading } = useHomerooms();

  // Only the groups whose register this person may actually open. The list
  // itself is every group in the school — the roster's filter and the student
  // form both need that — but a scope is a thing you can *act* on, and offering
  // a teacher the eleven groups that are not theirs would be eleven ways to land
  // on a 403. Filtered rather than disabled: a scope pill is not a place to
  // explain a permission.
  const homerooms = useMemo(
    () => allHomerooms.filter((candidate) => candidate.canTakeRegister),
    [allHomerooms]
  );

  // With nothing chosen yet, a group of the teacher's own before one the school
  // has opened to every teacher: a lower-school tutor offered the upper-school
  // groups as well came for their own, whichever sorts first. An open group is
  // the only kind this caller can take without being its teacher, so "not open"
  // is "theirs". An admin gets the same preference: the first group that is not
  // open, not simply the first group. A tutor whose own group is itself open has
  // nothing to tell it apart by here, and lands on whichever open group sorts first.
  const homeroom =
    homerooms.find((candidate) => candidate.id === selectedHomeroomId) ??
    homerooms.find((candidate) => !candidate.anyTeacherCanTakeAttendance) ??
    homerooms[0] ??
    null;

  // Written back only once the data has settled on a different answer, and only
  // from an effect: a store write during render is a render loop waiting for a
  // second subscriber.
  useEffect(() => {
    if (homeroom && homeroom.id !== selectedHomeroomId) setSelectedHomeroomId(homeroom.id);
  }, [homeroom, selectedHomeroomId, setSelectedHomeroomId]);

  return {
    homerooms,
    homeroom,
    selectHomeroom: setSelectedHomeroomId,
    isLoading,
    schoolHasHomerooms: allHomerooms.length > 0,
  };
}

/**
 * Declares that the page on screen is scoped to a homeroom, which is what puts
 * the Homeroom pill on the topbar. Released on unmount, so the pill leaves with
 * the page that owns it.
 */
export function useHomeroomScopePills(): void {
  const acquireScope = useHomeroomScopeStore((state) => state.acquireScope);
  const releaseScope = useHomeroomScopeStore((state) => state.releaseScope);

  useEffect(() => {
    acquireScope();
    return releaseScope;
  }, [acquireScope, releaseScope]);
}
